"use client";

import { useMemo } from "react";
import { useFormManager } from "@/lib/hooks/forms/use-form-manager";
import { useClientSearchHook } from "../../hooks/useClientSearch";
import { useGrantAccessMutationHook } from "../../hooks/useGrantAccessMutation";
import { useGrantAccessForm } from "../../hooks/useGrantAccessForm";
import {
  useEmployeeClientAccess,
  useRevokeEmployeeAccess,
} from "@/lib/hooks/api/admin";
import type { GrantEmployeeAccessFormData } from "@/lib/types/auth/grant-access";
import type { ClientRecord } from "@/lib/types/auth/grant-access";

interface UseGrantEmployeeAccessModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** When set, the modal grants this employee access (user-centric flow). */
  userId?: string;
}

/**
 * Form state + save logic for the standalone GrantEmployeeAccessModal
 * (the version that uses FormModal, not the global-modal-stack variant).
 *
 * Behavior preserved exactly from the original inline implementation.
 */
export function useGrantEmployeeAccessModal({
  isOpen,
  onClose,
  onSuccess,
  userId: preselectedUserId,
}: UseGrantEmployeeAccessModalArgs) {
  const { form, resetForm, setContactIds, setGrantToAllClients, setUserId } =
    useGrantAccessForm();

  // When the modal is opened for a specific employee (user-centric flow), seed
  // the form's userId. Without this the pre-selected employee renders in the UI
  // but the form value stays empty and the grant submits with no user.
  if (preselectedUserId && isOpen && form.getValues("userId") !== preselectedUserId) {
    setUserId(preselectedUserId);
  }

  const formManager = useFormManager<GrantEmployeeAccessFormData>({
    form,
    draftKey: "grant-employee-access",
    isOpen,
    onSuccess: () => {
      resetForm();
      onSuccess?.();
      onClose();
    },
    successMessage: "Employee access granted successfully!",
  });

  const {
    clientSearchTerm,
    setClientSearchTerm,
    displayedClients,
    clientsLoading,
    isClientSearching,
  } = useClientSearchHook();

  const { grantAccessMutation, grantAccessToMultiple } = useGrantAccessMutationHook();

  // Existing grants for the selected employee, so the modal can list them with
  // a Revoke control and dedupe already-granted contacts on submit.
  const effectiveUserId = preselectedUserId || form.watch("userId") || "";
  const { data: existingGrants, isLoading: existingLoading } =
    useEmployeeClientAccess(effectiveUserId);
  const revokeAccessMutation = useRevokeEmployeeAccess();

  const alreadyGrantedContactIds = useMemo(
    () => new Set((existingGrants ?? []).map((g) => g.contact_id)),
    [existingGrants]
  );

  const handleRevoke = (contactId: string) => {
    if (!effectiveUserId) return;
    if (confirm("Revoke this employee's access to the contact?")) {
      revokeAccessMutation.mutate({ userId: effectiveUserId, contactId });
    }
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      // Resolve the employee synchronously: the preselected prop (authoritative,
      // race-free) wins over the in-form pick. Never trust a reset/seed-timed
      // form value as the sole source of a required id.
      const targetUserId = preselectedUserId || values.userId || "";
      if (!targetUserId) {
        formManager.showError("Select an employee to grant access to.");
        return;
      }

      const { grantToAllClients, contactIds } = values;
      const requested: string[] = grantToAllClients
        ? (displayedClients || []).map((client: ClientRecord) => client.id)
        : (contactIds ?? []);

      // Dedupe contacts the employee already has access to — avoids duplicate
      // rows and a "already granted" failure from the grant mutation.
      const clientIdsToGrant = requested.filter(
        (id) => !alreadyGrantedContactIds.has(id)
      );

      if (clientIdsToGrant.length === 0) {
        formManager.showError(
          "Those contacts are already granted to this employee."
        );
        return;
      }

      await grantAccessToMultiple(clientIdsToGrant, targetUserId);
      formManager.showSuccess();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to grant access";
      formManager.showError(message);
    }
  });

  return {
    form,
    formManager,
    setContactIds,
    setGrantToAllClients,
    setUserId,
    clientSearchTerm,
    setClientSearchTerm,
    displayedClients,
    clientsLoading,
    isClientSearching,
    grantAccessMutation,
    handleSubmit,
    existingGrants: existingGrants ?? [],
    existingLoading,
    revokeAccessMutation,
    handleRevoke,
  };
}
